import { useState, useEffect } from 'react'

type ShopProduct = {
  title: string
  price: string
  image: string
  handle: string
}

const SHOP_API = 'https://webhub-6b3.pages.dev/api/shop-config'
const DEFAULT_HANDLES = [
  'tai-chi-glass-wide-mouth-pot',
  'tai-chi-teapot',
  'tai-chi-tiger',
  'tai-chi-fish-shaped-ceramic-plate'
]

export default function ShopSection() {
  const [products, setProducts] = useState<ShopProduct[]>([])

  useEffect(() => {
    let cancelled = false
    const doFetch = async () => {
      try {
        const resp = await fetch(SHOP_API)
        const data = await resp.json()
        const configs = data.configs || {}
        const hostname = window.location.hostname
        let handles: string[] | null = null

        for (const [key, cfg] of Object.entries(configs)) {
          if (hostname.includes(key)) {
            handles = cfg as unknown as string[]
            break
          }
        }

        if (!handles || handles.length === 0) handles = DEFAULT_HANDLES

        const results = await Promise.all(
          handles.map(function(h) {
            return fetch('https://lemaharmony.com/products/' + h + '.json')
              .then(function(r) { return r.json() })
              .then(function(d) {
                return {
                  title: d.product.title,
                  price: d.product.variants[0]?.price || '0',
                  image: d.product.images[0]?.src || '',
                  handle: d.product.handle
                }
              })
              .catch(function() { return null })
          })
        )
        if (!cancelled) {
          const filtered = results.filter(function(p) { return p !== null }) as ShopProduct[]
          setProducts(filtered)
          if (filtered.length === 0) {
            console.warn('[ShopSection] No valid products')
          }
        }
      } catch (e) {
        // Noop
      }
    }
    doFetch()
    return function() { cancelled = true }
  }, [])

  return (
    <section className="py-4 bg-gradient-to-r from-amber-50 via-white to-orange-50 border-y border-amber-200">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-3 gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center shrink-0">
              <span className="text-white text-sm">LEMA</span>
            </div>
            <div className="truncate">
              <h2 className="text-lg font-bold text-gray-900 truncate">LEMA Harmony</h2>
              <p className="text-gray-500 text-xs truncate">Exclusive cultural products</p>
            </div>
          </div>
          <a
            href="https://lemaharmony.com"
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 px-3 py-1.5 text-xs bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-orange-700 transition-all duration-200 shadow-sm"
          >
            Browse All →
          </a>
        </div>

        {products.length > 0 ? (
          <div className="grid grid-cols-4 gap-2">
            {products.map(function(product, idx) {
              const url = 'https://lemaharmony.com/products/' + product.handle
              return (
              <a
                key={idx}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white rounded-lg border border-gray-100 overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
              >
                <div className="aspect-square bg-gray-50 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="p-1.5">
                  <h3 className="text-xs font-semibold text-gray-900 truncate leading-tight">{product.title}</h3>
                  <p className="text-amber-700 font-bold text-xs mt-0.5">{product.price}</p>
                </div>
              </a>
              )
            })}
          </div>
        ) : (
          <div className="bg-white/60 rounded-lg py-4 text-center">
            <p className="text-gray-500 text-sm">Discover traditional craftsmanship</p>
            <a
              href="https://lemaharmony.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-2 px-4 py-1.5 text-xs bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-orange-700 transition-all duration-200"
            >
              Visit LEMA Harmony
            </a>
          </div>
        )}
      </div>
    </section>
  )
}
